// updated JS
import { RcContext } from "../context/rcContext";
import { CanvasImageSaverService } from "./canvasImageSaverService";
import { IDeviceCanvasRender, IRcAppUI, ISystemInfoUI } from "../mcrc-api";
import { IRcDeviceContext, IRemoteControlStreamingContext } from "../context/deviceContext";
import { IMonitorInfo } from "../definitions";
import { Injectable } from "@angular/core";

@Injectable({
    providedIn: 'root'
})
export class AppUIService implements IRcAppUI {
    private deviceCanvas: IDeviceCanvasRender;
    private hdSpinner = false;
    private spinnerCallback: (enable: boolean) => void;

    constructor(private canvasImageSaverService: CanvasImageSaverService) { }

    private _systemInfoUI: ISystemInfoUI;

    public setSystemInfoUI(component: ISystemInfoUI) {
        // DeviceInfoComponent
        this._systemInfoUI = component;
    }

    public getSystemInfoUI(): ISystemInfoUI {
        return this._systemInfoUI;
    }

    // the following elements used in jQuery selectors do not exist in XS 4.3
    //   #mcrc-dlg-disconnected, #session-closed, #disconnected-details-button, #disconnected-details
    //   #mcrc-top-bar-buttons,#crc-top-bar-compressed-menu,#mcrc-device-name-label
    //   #device-brand, .gritter-device-button, #mcrc-btn-device-info, #mcrc-btn-file-explorer
    //   #mcrc-compressed-menu-btn-file-explorer, #mcrc-compressed-menu-btn-device-info
    //   #mcrc-btn-2d3d-settings, #mcrc-compressed-menu-btn-2d3d-settings
    // JS code for these elements was removed

    // the following appUI calls handling those elements do not exist any more
    //   toggleTopBarControls(), toggle3d2dButton(), forceResize(), onPermissionsReady()

    public init(deviceCanvas: IDeviceCanvasRender) {
        this.deviceCanvas = deviceCanvas;
    }

    public takeScreenshot(options) {
        const context = RcContext.instance;
        const videoStreaming = context.deviceContext.videoStreaming;

        let canvasImage = videoStreaming
            ? (<IRemoteControlStreamingContext>context.deviceContext).deviceViewHandler.takeScreenshot(true)
            : this.deviceCanvas.getCanvasImage(options.skin);
        // console.log(canvasImage);
        const fileObj = this.canvasImageSaverService.save(
            canvasImage,
            options.name,
            options.type.toLowerCase(),
            options.bpp,
            options.quality,
            options.attachment
        );
        if (options.attachment) {
            options.fileObject = fileObj;
        }
    }

    public setCurrentMonitorRotation = (rotation) => {
        var currentMonitor = this.getCurrentMonitor();
        if (!currentMonitor) {
            return;
        }

        currentMonitor.rotation = rotation;
        currentMonitor.rotation$.next(rotation);
    }

    public getCurrentMonitor = (): IMonitorInfo => {
        var currentMonitorId = RcContext.instance.multiScreen.requestMonitorId;
        if (currentMonitorId === undefined || currentMonitorId === null) {
            return null;
        }

        return RcContext.instance.multiScreen.monitorList.find((monitor) => {
            return monitor.id === currentMonitorId;
        });
    }

    public disableMouseInput = (disable) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            ctx.disableMouseInput = disable;
        }
    }

    public showAnnotationsToolHint = (annotationsToolHint) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            ctx.annotationsToolHint = annotationsToolHint;
        }
    }

    public setAnnotationTool = (tool: string) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            switch (tool) {
                case 'pen':
                    ctx.annotationTool = 1;
                    break;
                case 'eraser':
                    ctx.annotationTool = 2;
                    break;
                default:
                    ctx.annotationTool = undefined;
            }
        }
    }

    public setAnnotationCursorSize = (size: number) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            ctx.annotationCursorSize = size;
        }
    }

    public setAnnotationCursorColor = (color: string) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            ctx.annotationCursorColor = color;
        }
    }

    public getDeviceBitDepth = (): number => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            return ctx.bitDepth;
        }
        // mcrc code relies on undefined for bitDepth not being set
        return RcContext.instance.bitDepth;
    }

    public setDeviceBitDepth = (depth: number) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            ctx.bitDepth = depth;
        }
        // as we can get a stale deviceContext from previous sessions
        RcContext.instance.bitDepth = depth;
    }

    public getDeviceImageQuality = (): number => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            return ctx.imageQuality;
        }
        // mcrc code relies on undefined for bitDepth not being set
        return RcContext.instance.imageQuality;
    }

    public setDeviceImageQuality = (quality: number) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            ctx.imageQuality = quality;
        }
        // as we can get a stale deviceContext from previous sessions
        RcContext.instance.imageQuality = quality;
    }

    public setAgentRecordingScreenWidth = (screenWidth: number) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            ctx.agentRecordingScreenWidth = screenWidth;
        }
    }

    public setAgentRecordingScreenHeight = (screenHeight: number) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            ctx.agentRecordingScreenHeight = screenHeight;
        }
    }

    public setAgentRecordingVideoFrameRate = (frameRate: number) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            ctx.agentRecordingVideoFrameRate = frameRate;
        }
    }

    public setAgentRecordingVideoBitRate = (videoBitRate: number) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            ctx.agentRecordingVideoBitRate = videoBitRate;
        }
    }

    public setAgentRecordingAudioSamplingRate = (audioSamplingRate) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            ctx.agentRecordingAudioSamplingRate = audioSamplingRate;
        }
    }

    public setAgentRecordingAudioBitRate = (audioBitRate) => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            ctx.agentRecordingAudioBitRate = audioBitRate;
        }
    }

    public getAgentRecordingAudioBitRate = () => {
        const ctx = RcContext.instance.deviceContext as IRcDeviceContext;
        if (ctx) {
            return ctx.agentRecordingAudioBitRate;
        }
    }

    /**
     * @deprecated: device-brand element doesn't exist any longer
     */
    public setOsType(osType: number) {
    }

    // unload: function () {
    //     if (mcrc.dal.socketBridge.isConnected()) {
    //         var message = mcrc.utils.messages.createStopMessage();
    //         mcrc.dal.socketBridge.send(message.getByteArray());
    //         mcrc.dal.socketBridge.close();
    //     }

    //     return null;
    // },

    public setSpinnerCallback(callback: (enable: boolean) => void) {
        this.spinnerCallback = callback;
    }

    public displayInProgressScreen() {
        if (this.spinnerCallback) {
            this.hdSpinner = true;
            this.spinnerCallback(true);
        }
    }

    public hideInProgressScreen() {
        if (this.spinnerCallback) {
            this.hdSpinner = false;
            this.spinnerCallback(false);
        }
    }

    public isInProgressScreenVisible() {
        return this.hdSpinner;
    }
}
